import React, { useState, FormEvent } from "react";
import { motion } from "framer-motion";
import { BsLinkedin, BsEnvelope, BsCheckCircle } from "react-icons/bs";
import Header from "../components/Header";
import { CONTACT_EMAIL, LINKEDIN_URL } from "../config";

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

const htmlTagRegex = /<\/?[a-z][\s\S]*?>/i;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateName = (name: string): string => {
  if (!name.trim()) return "Name is required";
  if (name.length > 100) return "Name is too long (max 100 characters)";
  if (htmlTagRegex.test(name)) return "Name cannot contain HTML tags";
  return "";
};

const validateEmail = (email: string): string => {
  if (!email.trim()) return "Email is required";
  if (email.length > 100) return "Email is too long (max 100 characters)";
  if (!emailRegex.test(email)) return "Please enter a valid email address";
  const [localPart, domain] = email.split("@");
  if (localPart.length > 64) return "Email local part is too long";
  if (domain.length > 255) return "Email domain is too long";
  return "";
};

const validateSubject = (subject: string): string => {
  if (!subject.trim()) return "Subject is required";
  if (subject.length > 150) return "Subject is too long (max 150 characters)";
  if (subject.length < 3) return "Please enter a more descriptive subject";
  if (htmlTagRegex.test(subject)) return "Subject cannot contain HTML tags";
  return "";
};

const validateMessage = (message: string): string => {
  if (!message.trim()) return "Message is required";
  if (message.length > 2000)
    return "Message is too long (max 2000 characters)";
  if (message.length < 5) return "Please enter a more detailed message";
  if (htmlTagRegex.test(message)) return "Message cannot contain HTML tags";
  return "";
};

const validateAll = (data: FormData): FormErrors => {
  const errors: FormErrors = {};
  const nameErr = validateName(data.name);
  const emailErr = validateEmail(data.email);
  const subjectErr = validateSubject(data.subject);
  const messageErr = validateMessage(data.message);
  if (nameErr) errors.name = nameErr;
  if (emailErr) errors.email = emailErr;
  if (subjectErr) errors.subject = subjectErr;
  if (messageErr) errors.message = messageErr;
  return errors;
};

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [sent, setSent] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    let error = "";
    if (name === "name") error = validateName(value);
    else if (name === "email") error = validateEmail(value);
    else if (name === "subject") error = validateSubject(value);
    else if (name === "message") error = validateMessage(value);

    setErrors((prev) => ({ ...prev, [name]: error || undefined }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setServerError("");
    const validationErrors = validateAll(formData);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(
        process.env.REACT_APP_WEB_FORM_EMAIL_SERVER_URI ||
          "http://localhost:5000/api/contact",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 429)
          throw new Error("Too many attempts. Please try again later.");
        if (response.status === 403)
          throw new Error("Access denied. Please try again later.");
        if (response.status === 500)
          throw new Error(data.error || "Server error. Please try again later.");
        throw new Error(data.error || "Failed to send message");
      }
      setSent(true);
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      setServerError(
        err instanceof Error ? err.message : "Failed to send message"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputBase =
    "w-full bg-white dark:bg-gray-800 border rounded-lg px-4 py-3 text-base text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-domichron-orange transition";

  return (
    <main className="min-h-screen bg-[#F9FAFB] dark:bg-gray-900 text-domichron-text dark:text-white font-sans antialiased">
      <Header />

      {/* Hero */}
      <section className="px-6 lg:px-20 pt-16 pb-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="max-w-3xl mx-auto space-y-5"
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold leading-tight">
            Let's build.
          </h1>
          <p className="text-lg text-gray-700 dark:text-gray-300">
            Using DomeScribe? Building something for creators or operators? Want
            to partner with Domichron? Send us a note — we read every message.
          </p>
        </motion.div>
      </section>

      {/* Two-column layout */}
      <section className="px-6 lg:px-20 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-6xl mx-auto">
          {/* Left: form */}
          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40 p-8 md:p-10 shadow-sm">
            <h2 className="text-2xl font-bold mb-6 text-domichron-text dark:text-white">
              Send a Message
            </h2>

            {sent ? (
              <div className="flex flex-col items-center justify-center text-center py-8 space-y-3">
                <BsCheckCircle className="w-10 h-10 text-domichron-orange" />
                <h3 className="text-xl font-bold text-domichron-text dark:text-white">
                  Message sent.
                </h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Thanks for reaching out — we'll be in touch soon.
                </p>
                <button
                  onClick={() => setSent(false)}
                  className="text-domichron-orange hover:underline text-sm mt-2"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-2"
                  >
                    Your Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Jane Doe"
                    className={`${inputBase} ${
                      errors.name
                        ? "border-red-500"
                        : "border-gray-300 dark:border-gray-700"
                    }`}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-2"
                  >
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    className={`${inputBase} ${
                      errors.email
                        ? "border-red-500"
                        : "border-gray-300 dark:border-gray-700"
                    }`}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="subject"
                    className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-2"
                  >
                    Subject
                  </label>
                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="What's this about?"
                    className={`${inputBase} ${
                      errors.subject
                        ? "border-red-500"
                        : "border-gray-300 dark:border-gray-700"
                    }`}
                  />
                  {errors.subject && (
                    <p className="text-red-500 text-sm mt-1">{errors.subject}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-2"
                  >
                    Your Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us what you're working on or what you'd like to talk about..."
                    rows={6}
                    className={`${inputBase} resize-none ${
                      errors.message
                        ? "border-red-500"
                        : "border-gray-300 dark:border-gray-700"
                    }`}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {errors.message ? (
                      <p className="text-red-500 text-sm">{errors.message}</p>
                    ) : (
                      <span />
                    )}
                    <span className="text-xs text-gray-500">
                      {formData.message.length}/2000
                    </span>
                  </div>
                </div>

                {serverError && (
                  <p className="text-red-500 text-sm">{serverError}</p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-domichron-orange text-white font-semibold py-3 rounded-lg hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            )}
          </div>

          {/* Right: contact info */}
          <div className="space-y-10">
            <div>
              <h2 className="text-2xl font-bold mb-6 text-domichron-text dark:text-white">
                Get in Touch
              </h2>
              <div className="space-y-5">
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="flex items-start gap-4 group"
                >
                  <span className="flex-shrink-0 w-10 h-10 rounded-full bg-domichron-orange/10 flex items-center justify-center">
                    <BsEnvelope className="w-5 h-5 text-domichron-orange" />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Email
                    </p>
                    <p className="text-base text-domichron-text dark:text-white group-hover:text-domichron-orange transition-colors">
                      {CONTACT_EMAIL}
                    </p>
                  </div>
                </a>
                <a
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 group"
                >
                  <span className="flex-shrink-0 w-10 h-10 rounded-full bg-domichron-orange/10 flex items-center justify-center">
                    <BsLinkedin className="w-5 h-5 text-domichron-orange" />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      LinkedIn
                    </p>
                    <p className="text-base text-domichron-text dark:text-white group-hover:text-domichron-orange transition-colors">
                      linkedin.com/company/domichron
                    </p>
                  </div>
                </a>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold mb-5 text-domichron-text dark:text-white">
                What we'd love to hear about
              </h2>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                <li className="flex gap-3">
                  <span className="text-domichron-orange flex-shrink-0">→</span>
                  <span>
                    Creators and teams using DomeScribe — feedback, feature
                    requests, and what would make it more useful
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-domichron-orange flex-shrink-0">→</span>
                  <span>
                    Podcasters, video creators, marketers, and educators working
                    with media day to day
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-domichron-orange flex-shrink-0">→</span>
                  <span>
                    Engineers and builders interested in the tooling behind
                    DomeScribe
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-domichron-orange flex-shrink-0">→</span>
                  <span>
                    Anyone who wants to partner with or build alongside
                    Domichron
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-gray-200 dark:border-gray-800">
        <div className="flex flex-col items-center gap-3 px-6">
          <a
            href={LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-base text-gray-700 dark:text-gray-300 hover:text-domichron-orange transition-colors"
          >
            <BsLinkedin className="w-5 h-5" />
            <span>Connect on LinkedIn</span>
          </a>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            © {new Date().getFullYear()} Domichron, Inc. All rights reserved.
          </p>
        </div>
      </footer>
    </main>
  );
};

export default Contact;
